import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DrawerItem } from 'helpers/drawer-config';
import { useGlobalContext } from 'context/GlobalContext';

type MobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  menuConfig: DrawerItem[];
  moreMenuConfig: DrawerItem[];
};

/**
 * MobileMenu Component
 *
 * Menú hamburguesa para dispositivos móviles.
 * Muestra un drawer lateral con todos los items del menú en formato vertical.
 *
 * @component
 * @param {MobileMenuProps} props - Las propiedades del componente
 * @returns {JSX.Element} El menú móvil
 */
function MobileMenu({
  isOpen,
  onClose,
  onToggle,
  menuConfig,
  moreMenuConfig
}: MobileMenuProps): JSX.Element {
  const { t } = useTranslation('global');

  return (
    <>
      {/* Icono hamburguesa - Solo visible en móvil */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #e0e0e0',
          px: 2
        }}
      >
        <IconButton
          onClick={onToggle}
          sx={{
            color: '#0E335C'
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Drawer lateral */}
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#FFFFFF'
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ fontWeight: 600, color: '#0E335C' }}>Menú</Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 0 }}>
          {menuConfig.map((item) => (
            <MobileMenuItem key={item.key} item={item} onClose={onClose} />
          ))}

          {/* Item "Más" */}
          <MobileMenuItem
            item={{
              key: 'topNav.more',
              title: t('topNav.more') || 'Más',
              icon: null,
              submenu: moreMenuConfig,
              permissions: []
            }}
            onClose={onClose}
          />
        </List>
      </Drawer>
    </>
  );
}

type MobileMenuItemProps = {
  item: DrawerItem;
  onClose: () => void;
  level?: number;
};

function MobileMenuItem({ item, onClose, level = 0 }: MobileMenuItemProps) {
  const { hasPerm } = useGlobalContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!hasPerm(item.permissions)) {
    return null;
  }

  const hasSubmenu = item.submenu && item.submenu.length > 0;

  const handleClick = () => {
    if (hasSubmenu) {
      setOpen(!open);
    } else if (item.path) {
      navigate(item.path);
      onClose();
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          pl: 2 + level * 2,
          py: 1.5,
          '&:hover': {
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            fontSize: '14px',
            color: '#0E335C',
            fontWeight: level === 0 ? 500 : 400
          }}
        />
        {hasSubmenu && (open ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>

      {hasSubmenu && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.submenu.map((subItem) => (
              <MobileMenuItem
                key={subItem.key}
                item={subItem}
                onClose={onClose}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export default MobileMenu;

